"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useTranslations } from "next-intl"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { requestPasswordReset } from "@/app/[locale]/auth/actions"
import { Heading } from "@/components/Heading"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSubmitButton,
  FormWrapper,
} from "@/components/shadcn/Form"
import { Input } from "@/components/shadcn/Input"
import { Spacer } from "@/components/Spacer"
import { Text } from "@/components/Text"
import { urls } from "@/constants/urls"
import { Link } from "@/utils/navigation"

const formSchema = z.object({
  email: z.email(),
})

export default function Page() {
  const t = useTranslations("auth")

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "" },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const res = await requestPasswordReset({ email: values.email })
    if (res?.data?.error) {
      toast.error(res.data?.error)
      return
    }

    if (res?.serverError || res?.validationErrors) {
      toast.error("Something went wrong.", {
        description: "Please try again later. If the problem persists, please let us know.",
      })

      return
    }

    toast.success("Password reset instructions sent.", {
      description: "Please check your email for instructions on how to reset your password.",
    })
  }

  return (
    <>
      <Heading order={1} size={2} className="text-center">
        {t("requestResetPasswordHeading")}
      </Heading>

      <Spacer className="h-10" />

      <p className="text-center text-sm leading-relaxed text-slate-500">{t("requestResetPasswordText")}</p>

      <Spacer className="h-10" />

      <FormWrapper {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            name="email"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("emailLabel")}</FormLabel>

                <FormControl>
                  <Input {...field} type="email" autoComplete="email" placeholder={t("emailPlaceholder")} />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <Spacer className="h-10" />

          <FormSubmitButton className="w-full" size="sm">
            {t("requestResetPasswordButton")}
          </FormSubmitButton>
        </form>
      </FormWrapper>

      <Spacer className="h-4" />

      <div className="flex justify-center gap-4">
        <Link href={urls.auth.signin}>
          <Text size="sm" variant="link">
            {t("requestResetPasswordBackToSignin")}
          </Text>
        </Link>
      </div>
    </>
  )
}
